import type { PlaybookId } from "@repo/app-types/entities/playbook";
import type {
  AdminApiPlaybookTestResult,
  AdminApiPlaybookTestRun,
  AdminApiPlaybookTestSpec,
  PlaybookTestSpecPatchPayload,
  PlaybookTestSpecPostPayload,
} from "@repo/app-types/entities/teammateAgent";
import {
  getTagsInvalidatedByCreate,
  getTagsInvalidatedByDelete,
  getTagsInvalidatedByUpdate,
  getTagsProvidedByEntity,
  getTagsProvidedByList,
  TagType,
} from "../helpers/tags.js";
import { api } from "../redux.js";

const playbookTestSpecApi = api.injectEndpoints({
  endpoints: (builder) => ({
    allPlaybookTestSpecs: builder.query<
      AdminApiPlaybookTestSpec[],
      { playbookId: PlaybookId },
      {
        data: {
          playbook_test_specs: AdminApiPlaybookTestSpec[];
        };
      }
    >({
      query: ({ playbookId }) =>
        `/recording-assessment-templates/${encodeURIComponent(playbookId)}/test-specs`,
      transformResponse: (response) => response.data.playbook_test_specs,
      providesTags: (result, error, { playbookId }) =>
        getTagsProvidedByList(TagType.PlaybookTestSpec, result, {
          filteredBy: [playbookId],
        }),
    }),
    getPlaybookTestSpec: builder.query<
      AdminApiPlaybookTestSpec,
      { playbookId: PlaybookId; testSpecId: string },
      {
        data: {
          playbook_test_spec: AdminApiPlaybookTestSpec;
        };
      }
    >({
      query: ({ playbookId, testSpecId }) =>
        `/recording-assessment-templates/${encodeURIComponent(playbookId)}/test-specs/${encodeURIComponent(testSpecId)}`,
      transformResponse: (response) => response.data.playbook_test_spec,
      providesTags: (_result, _error, { testSpecId }) =>
        getTagsProvidedByEntity(TagType.PlaybookTestSpec, testSpecId),
    }),
    createPlaybookTestSpec: builder.mutation<
      AdminApiPlaybookTestSpec,
      { playbookId: PlaybookId; payload: PlaybookTestSpecPostPayload },
      {
        data: {
          playbook_test_spec: AdminApiPlaybookTestSpec;
        };
      }
    >({
      query: ({ playbookId, payload }) => ({
        method: "POST",
        url: `/recording-assessment-templates/${encodeURIComponent(playbookId)}/test-specs`,
        body: payload,
      }),
      transformResponse: (response) => response.data.playbook_test_spec,
      async onQueryStarted({ playbookId }, { dispatch, queryFulfilled }) {
        const { data: newTestSpec } = await queryFulfilled;
        // Optimistically update the cache for the allPlaybookTestSpecs query
        // Note: We don't need to catch and undo here because we're updating
        // AFTER the mutation succeeds (await queryFulfilled).
        dispatch(
          playbookTestSpecApi.util.updateQueryData(
            "allPlaybookTestSpecs",
            { playbookId },
            (draft) => {
              draft.push(newTestSpec);
            },
          ),
        );
      },
      invalidatesTags: (result, error, { playbookId }) =>
        getTagsInvalidatedByCreate(TagType.PlaybookTestSpec, {
          inLists: [{ filteredBy: [playbookId] }],
        }),
    }),
    updatePlaybookTestSpec: builder.mutation<
      AdminApiPlaybookTestSpec,
      {
        playbookId: PlaybookId;
        testSpecId: string;
        payload: PlaybookTestSpecPatchPayload;
      },
      {
        data: {
          playbook_test_spec: AdminApiPlaybookTestSpec;
        };
      }
    >({
      query: ({ playbookId, testSpecId, payload }) => ({
        method: "PATCH",
        url: `/recording-assessment-templates/${encodeURIComponent(playbookId)}/test-specs/${encodeURIComponent(testSpecId)}`,
        body: payload,
      }),
      transformResponse: (response) => response.data.playbook_test_spec,
      invalidatesTags: (result, error, { testSpecId }) =>
        getTagsInvalidatedByUpdate(TagType.PlaybookTestSpec, testSpecId),
    }),
    deletePlaybookTestSpec: builder.mutation<
      void,
      { playbookId: PlaybookId; testSpecId: string }
    >({
      query: ({ playbookId, testSpecId }) => ({
        method: "DELETE",
        url: `/recording-assessment-templates/${encodeURIComponent(playbookId)}/test-specs/${encodeURIComponent(testSpecId)}`,
      }),
      invalidatesTags: (result, error, { testSpecId }) =>
        getTagsInvalidatedByDelete(TagType.PlaybookTestSpec, testSpecId),
    }),
    getPlaybookTestRun: builder.query<
      AdminApiPlaybookTestRun,
      { playbookId: PlaybookId; testSpecId: string; testRunId: string },
      {
        data: {
          playbook_test_run: AdminApiPlaybookTestRun;
        };
      }
    >({
      query: ({ playbookId, testSpecId, testRunId }) =>
        `/recording-assessment-templates/${encodeURIComponent(playbookId)}/test-specs/${encodeURIComponent(testSpecId)}/test-runs/${encodeURIComponent(testRunId)}`,
      transformResponse: (response) => response.data.playbook_test_run,
      providesTags: (result, error, { testRunId }) => [
        { type: TagType.PlaybookTestRun, id: testRunId },
      ],
    }),
    getPlaybookTestResult: builder.query<
      AdminApiPlaybookTestResult,
      { playbookId: PlaybookId; testSpecId: string; testResultId: string },
      {
        data: {
          playbook_test_result: AdminApiPlaybookTestResult;
        };
      }
    >({
      query: ({ playbookId, testSpecId, testResultId }) =>
        `/recording-assessment-templates/${encodeURIComponent(playbookId)}/test-specs/${encodeURIComponent(testSpecId)}/test-results/${encodeURIComponent(testResultId)}`,
      transformResponse: (response) => response.data.playbook_test_result,
      providesTags: (result, error, { testResultId }) => [
        { type: TagType.PlaybookTestResult, id: testResultId },
      ],
    }),
  }),
});

export const {
  useAllPlaybookTestSpecsQuery,
  useGetPlaybookTestSpecQuery,
  useCreatePlaybookTestSpecMutation,
  useUpdatePlaybookTestSpecMutation,
  useDeletePlaybookTestSpecMutation,
  useGetPlaybookTestResultQuery,
  useGetPlaybookTestRunQuery,
} = playbookTestSpecApi;
